import { TDisplayDefinitionEntryNumber } from '@internal/utils/displayDefinition/entry/DisplayDefinitionEntryNumber'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute'
import { RangeInput } from '@renderer-ui-library/molecules'
import { useRangeFilterOptions } from 'blocks/SearchResultsBlock/useRangeFilterOptions'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useMemo } from 'react'

export type RangeSelectValue = number | undefined

type Props = {
  disabled?: boolean
  config: TDisplayDefinitionEntryNumber
  min: number | undefined
  max: number | undefined
  onChange: (
    machineAttribute: MachineAttribute,
    value: {
      min?: RangeSelectValue
      max?: RangeSelectValue
    }
  ) => void
}

export const SearchRangeFilter: React.FC<Props> = React.memo(
  ({ onChange, config, min, max, disabled }) => {
    const { t } = useTranslation(localeNamespace.common)
    const machineAttribute = config.machineAttribute
    const rangeFilterOptions = useRangeFilterOptions(
      machineAttribute,
      config.label
    )

    const handleChange = useCallback(
      (value: { min?: RangeSelectValue; max?: RangeSelectValue }) =>
        onChange(machineAttribute, value),
      [machineAttribute, onChange]
    )

    const rangeFilter = useMemo(() => {
      let labels = {
        minLabel: '',
        maxLabel: '',
      }
      switch (config.machineAttribute) {
        case MachineAttribute.YEAR_OF_PRODUCTION:
          labels = {
            minLabel: t(
              translations.searchDesktopOverlayYearFromLabel
            ).toString(),
            maxLabel: t(
              translations.searchDesktopOverlayYearToLabel
            ).toString(),
          }
          break

        case MachineAttribute.ENGINE_HP:
          labels = {
            minLabel: t(
              translations.searchDesktopOverlayPowerFromLabel
            ).toString(),
            maxLabel: t(
              translations.searchDesktopOverlayPowerToLabel
            ).toString(),
          }
          break

        case MachineAttribute.PRICE_NET_EUR:
          labels = {
            minLabel: t(
              translations.searchDesktopOverlayPriceFromLabel
            ).toString(),
            maxLabel: t(
              translations.searchDesktopOverlayPriceToLabel
            ).toString(),
          }
          break
      }

      return {
        ...rangeFilterOptions,
        ...labels,
      }
    }, [config.machineAttribute, rangeFilterOptions, t])

    return (
      <RangeInput
        {...rangeFilter}
        unit={undefined}
        minValue={min}
        maxValue={max}
        noSeparator
        onChange={handleChange}
      />
    )
  }
)

SearchRangeFilter.displayName = 'SearchRangeFilter'
